import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type {
  ColumnDef,
  PaginationState,
  SortingState,
  Updater,
} from "@tanstack/react-table";

import { PaginationControls } from "~/components";
import { Table } from "~/ui/Table/Table";

export type FormErrorType = string | boolean;

export interface PaginatedTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  pageSize: number;
  currentPage: number;
  setCurrentPage: (p: number) => void;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
  totalRows?: number;
  isLoadingData: boolean;
}

export const PaginatedTable = <TData,>({
  data,
  columns,
  pageSize,
  currentPage,
  setCurrentPage,
  sorting,
  setSorting,
  totalRows,
  isLoadingData,
}: PaginatedTableProps<TData>) => {
  const totalPages =
    totalRows === undefined ? undefined : Math.ceil(totalRows / pageSize);

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    if (updater instanceof Function) {
      const params = updater({ pageIndex: currentPage - 1, pageSize });
      setCurrentPage(params.pageIndex + 1);
    }
  };

  const handleSortChange = (updater: Updater<SortingState>) => {
    if (updater instanceof Function) {
      const params = updater(sorting);
      setSorting(params);
    }
  };

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: true,
    pageCount: totalPages,
    state: {
      pagination: { pageIndex: currentPage - 1, pageSize },
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortChange,
  });

  return (
    <>
      <Table
        isLoadingData={isLoadingData}
        table={table}
        rowsPerPage={pageSize}
        className="rounded-b-none"
      />
      {totalPages !== undefined && (
        <PaginationControls
          currentPage={currentPage}
          numPages={totalPages}
          setPage={(i) => table.setPageIndex(i - 1)}
          className="flex w-full items-center gap-2 rounded-b-xl border-t border-neutral-variant-30 bg-surface-container-high p-6 dark:border-neutral-variant-80 dark:bg-surface-container-high-dark"
        />
      )}
    </>
  );
};
PaginatedTable.displayName = "PaginatedTable";
